import React, { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Database,
  Table2,
  Loader2,
  Search,
  ChevronRight,
  ChevronDown,
  Key,
  Link2,
  Copy,
  Eye,
  FileCode,
  RefreshCw,
  Star,
  Boxes,
} from "lucide-react";

export default function DbExplorer() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tablesData, setTablesData] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableDetail, setTableDetail] = useState(null); // { columns, primary_key, indexes }
  const [tableRows, setTableRows] = useState(null);
  const [ddl, setDdl] = useState("");
  const [activeTab, setActiveTab] = useState("data"); // data | schema | ddl
  const [detailLoading, setDetailLoading] = useState(false);
  const [q, setQ] = useState("");
  // v2.9.1 · Filtro por columna (Odoo Ref cuando la tabla es master_ot)
  const [colFilter, setColFilter] = useState({ col: "", val: "" });
  // v92.0 · Búsqueda ILIKE para odoo_ip_address
  const [ipSearch, setIpSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState({
    master: true,
    star_facts: true,
    star_dimensions: true,
    operations: true,
    inventory: true,
    auth: false,
    catalog: false,
    system: false,
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    loadTables();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      loadTableDetail(selectedTable);
      setPage(0);
      setColFilter({ col: "", val: "" });  // v2.9.1 · reset filtro al cambiar tabla
      setIpSearch("");  // v92.0 · reset búsqueda IP
    }
  }, [selectedTable]);

  useEffect(() => {
    if (selectedTable && activeTab === "data") loadRows(selectedTable, page);
    if (selectedTable && activeTab === "ddl" && !ddl) loadDdl(selectedTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, activeTab, page, colFilter.val, ipSearch]);

  const loadTables = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/v1/db-explorer/tables");
      setTablesData(data);
      if (!selectedTable && data.tables.length) {
        // Seleccionar master_work_order si existe, sino la primera
        const master = data.tables.find((t) => t.name === "master_work_order");
        setSelectedTable(master?.name || data.tables[0].name);
      }
    } catch (e) {
      toast.error("Error cargando tablas: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadTableDetail = async (name) => {
    setDetailLoading(true);
    setDdl("");
    try {
      const { data } = await api.get(`/v1/db-explorer/tables/${name}/columns`);
      setTableDetail(data);
    } catch (e) {
      toast.error("Error cargando columnas: " + (e.response?.data?.detail || e.message));
    } finally {
      setDetailLoading(false);
    }
  };

  const loadRows = async (name, pageNum) => {
    try {
      const params = { limit: PAGE_SIZE, offset: pageNum * PAGE_SIZE };
      if (colFilter.col && colFilter.val) {
        params.where_col = colFilter.col;
        params.where_val = colFilter.val;
      }
      // v92.0 · Búsqueda ILIKE para IPs
      if (ipSearch.trim() && name === "odoo_ip_address") {
        params.search_col = "name";
        params.search = ipSearch.trim();
      }
      const { data } = await api.get(`/v1/db-explorer/tables/${name}/rows`, { params });
      setTableRows(data);
    } catch (e) {
      setTableRows({ rows: [], total: 0, error: e.response?.data?.detail || e.message });
    }
  };

  const loadDdl = async (name) => {
    try {
      const { data } = await api.get(`/v1/db-explorer/tables/${name}/ddl`);
      setDdl(data.ddl);
    } catch (e) {
      setDdl("-- Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const filteredGrouped = useMemo(() => {
    if (!tablesData) return {};
    const groups = tablesData.grouped;
    if (!q) return groups;
    const qLow = q.toLowerCase();
    const filtered = {};
    for (const [cat, tables] of Object.entries(groups)) {
      const match = tables.filter((t) => t.name.toLowerCase().includes(qLow));
      if (match.length) filtered[cat] = match;
    }
    return filtered;
  }, [tablesData, q]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const totalPages = tableRows ? Math.ceil(tableRows.total / PAGE_SIZE) : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="db-explorer-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Database className="w-7 h-7 text-emerald-600" />
              Explorador PostgreSQL
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {tablesData?.total_tables ?? 0} tablas · {(tablesData?.total_rows ?? 0).toLocaleString()} filas totales
            </p>
          </div>
          <button
            onClick={loadTables}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            data-testid="refresh-tables-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar tablas */}
          <aside className="col-span-4 xl:col-span-3 bg-white rounded-lg border border-slate-200 p-3 max-h-[75vh] overflow-y-auto">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar tabla..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="table-search-input"
              />
            </div>
            {Object.entries(filteredGrouped).map(([cat, tables]) => (
              <CategoryGroup
                key={cat}
                cat={cat}
                label={tablesData.category_labels[cat] || cat}
                tables={tables}
                expanded={expandedCats[cat] ?? true}
                onToggle={() => setExpandedCats({ ...expandedCats, [cat]: !(expandedCats[cat] ?? true) })}
                selected={selectedTable}
                onSelect={setSelectedTable}
              />
            ))}
          </aside>

          {/* Detalle tabla */}
          <main className="col-span-8 xl:col-span-9">
            {!selectedTable ? (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-500">
                Selecciona una tabla para ver sus detalles.
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200">
                {/* Table header */}
                <div className="border-b border-slate-200 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <Table2 className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-semibold text-slate-900 font-mono">{selectedTable}</h2>
                    {tablesData?.tables.find((t) => t.name === selectedTable)?.is_mirror && (
                      <span className="px-2 py-0.5 bg-cyan-100 text-cyan-800 text-xs rounded-full font-medium">
                        MIRROR (Odoo)
                      </span>
                    )}
                    {tablesData?.tables.find((t) => t.name === selectedTable)?.is_view && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                        VIEW
                      </span>
                    )}
                    <span className="ml-auto text-sm text-slate-500">
                      {tableRows?.total?.toLocaleString() ?? 0} filas · {tableDetail?.columns?.length ?? 0} columnas
                    </span>
                  </div>
                  {/* Tabs */}
                  <div className="flex gap-1 mt-4">
                    {[
                      { k: "data", label: "Datos", icon: Eye },
                      { k: "schema", label: "Esquema", icon: FileCode },
                      { k: "ddl", label: "DDL", icon: FileCode },
                    ].map(({ k, label, icon: Icon }) => (
                      <button
                        key={k}
                        onClick={() => setActiveTab(k)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${
                          activeTab === k
                            ? "bg-emerald-100 text-emerald-800 font-medium"
                            : "text-slate-600 hover:bg-slate-100"
                        }`}
                        data-testid={`tab-${k}-btn`}
                      >
                        <Icon className="w-4 h-4" /> {label}
                      </button>
                    ))}
                  </div>
                  {/* v2.9.1 · Buscador Odoo Ref (solo master_ot) */}
                  {selectedTable === "master_ot" && activeTab === "data" && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-slate-600 font-medium">🔎 Buscar por Odoo Ref (odoo_task_id):</label>
                      <input
                        type="number"
                        placeholder="Ej. 998137"
                        value={colFilter.col === "odoo_task_id" ? colFilter.val : ""}
                        onChange={(e) => setColFilter({ col: "odoo_task_id", val: e.target.value })}
                        className="px-2 py-1 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono w-40"
                        data-testid="master-ot-odoo-ref-input"
                      />
                      {colFilter.val && (
                        <button
                          onClick={() => setColFilter({ col: "", val: "" })}
                          className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                          data-testid="master-ot-odoo-ref-clear"
                        >
                          Limpiar
                        </button>
                      )}
                      {tableRows?.where_col === "odoo_task_id" && (
                        <span className="text-xs text-emerald-700 font-mono">
                          {tableRows.total} resultado(s)
                        </span>
                      )}
                    </div>
                  )}
                  {/* v92.0 · Buscador de IPs (solo odoo_ip_address) */}
                  {selectedTable === "odoo_ip_address" && activeTab === "data" && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="ip-search-bar">
                      <Search className="w-4 h-4 text-slate-400" />
                      <label className="text-xs text-slate-600 font-medium">Buscar IP:</label>
                      <input
                        type="text"
                        placeholder='Ej. "10.200" o "10.200.42.3"'
                        value={ipSearch}
                        onChange={(e) => { setIpSearch(e.target.value); setPage(0); }}
                        className="px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono w-56"
                        data-testid="ip-search-input"
                      />
                      {ipSearch && (
                        <button
                          onClick={() => { setIpSearch(""); setPage(0); }}
                          className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                          data-testid="ip-search-clear"
                        >
                          Limpiar
                        </button>
                      )}
                      {ipSearch.trim() && tableRows && (
                        <span className="text-xs font-mono" style={{ color: tableRows.total > 0 ? "#059669" : "#DC2626" }}>
                          {tableRows.total.toLocaleString()} resultado(s)
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                {detailLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                  </div>
                ) : (
                  <>
                    {activeTab === "schema" && tableDetail && (
                      <SchemaView detail={tableDetail} />
                    )}
                    {activeTab === "data" && tableRows && (
                      <DataView
                        rows={tableRows.rows}
                        columns={tableDetail?.columns || []}
                        total={tableRows.total}
                        page={page}
                        totalPages={totalPages}
                        pageSize={PAGE_SIZE}
                        onPageChange={setPage}
                      />
                    )}
                    {activeTab === "ddl" && (
                      <div className="p-5">
                        <div className="flex justify-end mb-2">
                          <button
                            onClick={() => copyToClipboard(ddl)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                            data-testid="copy-ddl-btn"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copiar
                          </button>
                        </div>
                        <pre className="bg-slate-950 text-emerald-300 p-4 rounded-lg text-xs overflow-x-auto font-mono">
                          {ddl || "Cargando..."}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}

// ────────────────────────────────────────────────────────────────

function CategoryGroup({ cat, label, tables, expanded, onToggle, selected, onSelect }) {
  const IconMap = { star_facts: Star, star_dimensions: Star, master: Boxes };
  const Icon = IconMap[cat];
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-slate-700 uppercase tracking-wide hover:bg-slate-50 rounded"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="flex-1 text-left">{label}</span>
        <span className="text-slate-400 font-normal">{tables.length}</span>
      </button>
      {expanded && (
        <div className="ml-3 mt-0.5 space-y-0.5">
          {tables.map((t) => (
            <button
              key={t.name}
              onClick={() => onSelect(t.name)}
              className={`w-full text-left px-2 py-1.5 rounded text-xs font-mono flex items-center gap-2 ${
                selected === t.name ? "bg-emerald-100 text-emerald-900" : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`table-item-${t.name}`}
            >
              <span className="flex-1 truncate">{t.name}</span>
              {t.is_mirror && <span className="text-[10px] px-1 bg-cyan-100 text-cyan-700 rounded">M</span>}
              {t.is_view && <span className="text-[10px] px-1 bg-purple-100 text-purple-700 rounded">V</span>}
              <span className="text-[10px] text-slate-400">{t.row_count.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SchemaView({ detail }) {
  return (
    <div className="p-5 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Columnas</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Columna</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-center">Nullable</th>
                <th className="px-3 py-2 text-left">Default</th>
                <th className="px-3 py-2 text-left">Claves</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {detail.columns.map((c) => (
                <tr key={c.column_name} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-slate-400">{c.ordinal_position}</td>
                  <td className="px-3 py-1.5 font-mono font-medium text-slate-900">{c.column_name}</td>
                  <td className="px-3 py-1.5 text-slate-600">
                    <span className="font-mono text-xs">
                      {c.udt_name}
                      {c.character_maximum_length ? `(${c.character_maximum_length})` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {c.is_nullable === "YES" ? (
                      <span className="text-slate-400">·</span>
                    ) : (
                      <span className="text-red-500 text-xs font-bold">NOT NULL</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-500 max-w-[200px] truncate">
                    {c.column_default || "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex gap-1">
                      {c.is_primary_key && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] rounded font-bold">
                          <Key className="w-3 h-3" /> PK
                        </span>
                      )}
                      {c.foreign_key && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded font-mono">
                          <Link2 className="w-3 h-3" />
                          {c.foreign_key.references_table}.{c.foreign_key.references_column}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail.indexes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Índices ({detail.indexes.length})</h3>
          <div className="space-y-1">
            {detail.indexes.map((idx) => (
              <div key={idx.name} className="bg-slate-50 px-3 py-2 rounded text-xs font-mono">
                <span className="text-emerald-700 font-semibold">{idx.name}</span>
                <div className="text-slate-500 mt-0.5">{idx.definition}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DataView({ rows, columns, total, page, totalPages, pageSize, onPageChange }) {
  if (!rows.length) {
    return (
      <div className="p-12 text-center text-slate-500">
        {total === 0 ? "Tabla vacía." : "Sin resultados."}
      </div>
    );
  }
  // v2.9.9 · Columnas de visibilidad prioritaria (pinned al inicio + highlight amarillo)
  // Se aplican en cualquier tabla; sólo tienen efecto cuando existen.
  const PRIORITY_COLS = ["odoo_task_id", "stage_id_odoo", "stage_name", "fsm_done", "odoo_write_date"];
  const allCols = columns.map((c) => c.column_name);
  const priority = PRIORITY_COLS.filter((c) => allCols.includes(c));
  const rest = allCols.filter((c) => !PRIORITY_COLS.includes(c));
  const cols = [...priority, ...rest];
  const isPriority = (c) => PRIORITY_COLS.includes(c);
  return (
    <div>
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-slate-700 sticky top-0">
            <tr>
              {cols.map((col) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left font-mono font-semibold whitespace-nowrap"
                  style={isPriority(col) ? { background: "#FEF9C3", color: "#713F12", fontWeight: 800 } : undefined}
                  data-testid={`db-col-${col}`}
                >
                  {col}{isPriority(col) && <span className="ml-1 text-[9px] text-amber-600">★</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50">
                {cols.map((col) => (
                  <td
                    key={col}
                    className="px-3 py-1.5 font-mono whitespace-nowrap max-w-[300px] truncate text-slate-700"
                    style={isPriority(col) ? { background: "#FEFCE8", fontWeight: 600 } : undefined}
                  >
                    {formatCell(col, row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-600">
        <span>
          Página {page + 1} de {totalPages || 1} · Mostrando {rows.length} de {total.toLocaleString()} filas
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0}
            className="px-3 py-1 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50"
            data-testid="prev-page-btn"
          >
            ← Anterior
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50"
            data-testid="next-page-btn"
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
}

// v2.9.9 · Render específico con badges para columnas clave
function formatCell(col, v) {
  if (col === "fsm_done") {
    if (v === true) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#FEE2E2", color: "#991B1B" }}>true (zombi)</span>;
    if (v === false) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#DCFCE7", color: "#166534" }}>false</span>;
    return <span className="text-slate-400">NULL</span>;
  }
  if (col === "stage_id_odoo") {
    if (v == null) return <span className="text-slate-400">NULL</span>;
    const ok = Number(v) === 22;
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: ok ? "#DCFCE7" : "#FEE2E2", color: ok ? "#166534" : "#991B1B" }}>
        {v}{ok ? " ✓" : " ✗"}
      </span>
    );
  }
  return formatValue(v);
}

function formatValue(v) {
  if (v === null || v === undefined) return <span className="text-slate-400">NULL</span>;
  if (typeof v === "boolean") return v ? <span className="text-emerald-600">✓</span> : <span className="text-red-500">✗</span>;
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}
